/**
 * Validation utilities for form inputs
 */

/** Email validation regex */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Phone validation regex (US format, flexible) */
const PHONE_REGEX = /^\+?1?\s*\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/

/** Minimum name length */
export const NAME_MIN_LENGTH = 2

/** Maximum name length */
export const NAME_MAX_LENGTH = 100

/** Minimum message length */
export const MESSAGE_MIN_LENGTH = 10

/** Maximum message length */
export const MESSAGE_MAX_LENGTH = 1000

/**
 * Validates an email address
 * @param email - Email address to validate
 * @returns True if valid, false otherwise
 */
export const validateEmail = (email: string): boolean => {
  if (!email || email.trim().length === 0) {
    return false
  }
  return EMAIL_REGEX.test(email.trim())
}

/**
 * Gets error message for invalid email
 * @param email - Email address
 * @returns Error message or empty string if valid
 */
export const getEmailError = (email: string): string => {
  if (!email || email.trim().length === 0) {
    return 'Email is required'
  }
  if (!validateEmail(email)) {
    return 'Please enter a valid email address'
  }
  return ''
}

/**
 * Validates a phone number (flexible US format)
 * @param phone - Phone number to validate
 * @returns True if valid, false otherwise
 */
export const validatePhone = (phone: string): boolean => {
  if (!phone || phone.trim().length === 0) {
    return true // Phone is optional
  }
  return PHONE_REGEX.test(phone.trim())
}

/**
 * Gets error message for invalid phone
 * @param phone - Phone number
 * @returns Error message or empty string if valid
 */
export const getPhoneError = (phone: string): string => {
  if (!phone || phone.trim().length === 0) {
    return '' // Phone is optional
  }
  if (!validatePhone(phone)) {
    return 'Please enter a valid phone number (e.g., (555) 123-4567)'
  }
  return ''
}

/**
 * Validates a name field
 * @param name - Name to validate
 * @returns True if valid, false otherwise
 */
export const validateName = (name: string): boolean => {
  if (!name || name.trim().length === 0) {
    return false
  }
  const trimmedName = name.trim()
  return trimmedName.length >= NAME_MIN_LENGTH && trimmedName.length <= NAME_MAX_LENGTH
}

/**
 * Gets error message for invalid name
 * @param name - Name
 * @returns Error message or empty string if valid
 */
export const getNameError = (name: string): string => {
  if (!name || name.trim().length === 0) {
    return 'Name is required'
  }
  const trimmedName = name.trim()
  if (trimmedName.length < NAME_MIN_LENGTH) {
    return `Name must be at least ${NAME_MIN_LENGTH} characters`
  }
  if (trimmedName.length > NAME_MAX_LENGTH) {
    return `Name must be no more than ${NAME_MAX_LENGTH} characters`
  }
  return ''
}

/**
 * Validates a message field
 * @param message - Message to validate
 * @returns True if valid, false otherwise
 */
export const validateMessage = (message: string): boolean => {
  if (!message || message.trim().length === 0) {
    return false
  }
  const trimmedMessage = message.trim()
  return trimmedMessage.length >= MESSAGE_MIN_LENGTH && trimmedMessage.length <= MESSAGE_MAX_LENGTH
}

/**
 * Gets error message for invalid message
 * @param message - Message
 * @returns Error message or empty string if valid
 */
export const getMessageError = (message: string): string => {
  if (!message || message.trim().length === 0) {
    return 'Message is required'
  }
  const trimmedMessage = message.trim()
  if (trimmedMessage.length < MESSAGE_MIN_LENGTH) {
    return `Message must be at least ${MESSAGE_MIN_LENGTH} characters`
  }
  if (trimmedMessage.length > MESSAGE_MAX_LENGTH) {
    return `Message must be no more than ${MESSAGE_MAX_LENGTH} characters`
  }
  return ''
}
